import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  isKakaoTalkInAppBrowser,
  isIOS,
  openInExternalBrowser,
  getInAppBrowserInfo,
} from '../utils/browserUtils';
import { BrandLogo } from '../design-system/components';
import { BENEFIT_INTENT_KEY, type BenefitIntent } from '../design-system/components/section/BenefitSection';
import './LoginPage.css';

/**
 * 로그인 페이지 (카카오 로그인 전용)
 * - 개인정보 처리방침 동의 (필수)
 * - 서비스 이용약관 동의 (필수)
 * - 마케팅(광고성 정보) 수신 동의 (선택) — 카카오톡·문자·전화
 *
 * 2026-10-05 대표: "필수만 동의하고 선택(마케팅)을 자꾸 빼고 로그인한다" → '마케팅 정보 수신'(광고처럼 들림)을
 * 고객이 얻는 것('할인·분양 소식 받기')으로 바꾸고 카드로 강조. 선택 항목은 법상 미리 체크하지 않는다(빈 칸 유지).
 * 놓친 회원은 혜택을 본 자리에서 한 번 더 제안한다(property-detail/BenefitAlertOptIn).
 */
const LoginPage: React.FC = () => {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { isAuthenticated, loginWithKakao } = useAuth();

  // 상세 '혜택 보기' 버튼으로 들어온 경우 (2026-10-01 혜택 잠금)
  const [searchParams] = useSearchParams();
  const fromBenefit = searchParams.get('reason') === 'benefit';
  // 상세 '혜택 보기'에서 넘어온 현장 — 이 현장 혜택 안내 요청(상담 연락 동의)을 로그인과 함께 받는다(2026-10-08 대표 '추천안')
  const [benefitIntent] = useState<BenefitIntent | null>(() => {
    if (!fromBenefit) return null;
    try {
      const v = JSON.parse(localStorage.getItem(BENEFIT_INTENT_KEY) || 'null');
      return v && typeof v.id === 'number' ? v : null;
    } catch {
      return null;
    }
  });

  // 인앱 브라우저 감지
  const [inAppBrowserInfo] = useState(() => getInAppBrowserInfo());
  const isInKakaoTalk = isKakaoTalkInAppBrowser();
  // 카카오톡 인앱은 카카오 로그인이 가장 매끄러운 곳(계정 입력 없이 동의만) — 외부 브라우저로 보내면
  // 돌아올 현장 주소(localStorage)도 잃는다. 로그인 화면에서는 카카오톡 경고를 띄우지 않는다.
  const showInAppWarning = inAppBrowserInfo.isInApp && !isInKakaoTalk;
  const [showClipboardAlert, setShowClipboardAlert] = useState(false);

  // 동의 상태
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreeMarketing, setAgreeMarketing] = useState(false);
  const [agreeAll, setAgreeAll] = useState(false);

  // 이미 로그인된 경우 홈으로 리다이렉트
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  // 전체 동의 체크박스 처리
  const handleAgreeAll = (checked: boolean) => {
    setAgreeAll(checked);
    setAgreePrivacy(checked);
    setAgreeTerms(checked);
    setAgreeMarketing(checked);
  };

  // 개별 체크박스 변경 시 전체 동의 상태 업데이트
  useEffect(() => {
    if (agreePrivacy && agreeTerms && agreeMarketing) {
      setAgreeAll(true);
    } else {
      setAgreeAll(false);
    }
  }, [agreePrivacy, agreeTerms, agreeMarketing]);

  // 필수 동의 여부 확인
  const canProceed = agreePrivacy && agreeTerms;

  // 외부 브라우저에서 열기
  const handleOpenExternalBrowser = () => {
    const needsClipboardAlert = openInExternalBrowser();
    if (needsClipboardAlert) {
      setShowClipboardAlert(true);
    }
  };

  const handleKakaoLogin = async () => {
    if (!canProceed) {
      setError('필수 약관에 동의해주세요.');
      return;
    }

    setLoading(true);
    setError('');

    // 동의 정보를 localStorage에 저장 (카카오 콜백에서 사용)
    localStorage.setItem('consent_privacy', 'true');
    localStorage.setItem('consent_terms', 'true');
    localStorage.setItem('consent_marketing', agreeMarketing ? 'true' : 'false');
    localStorage.setItem('consent_date', new Date().toISOString());
    // 안내를 보고 로그인 → 돌아가면 상세가 이 현장 혜택 안내를 자동 요청한다
    if (benefitIntent) {
      try {
        localStorage.setItem(BENEFIT_INTENT_KEY, JSON.stringify({ ...benefitIntent, ack: true }));
      } catch {
        /* 저장 실패 — 상세에서 '이 현장 혜택 보기'를 한 번 누르면 된다 */
      }
    }

    try {
      await loginWithKakao();
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || '카카오 로그인에 실패했습니다.';
      setError(errorMessage);
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-box">
          <div className="login-header">
            <div className="login-logo">
              <BrandLogo height={44} />
            </div>
            <p className="login-tagline">
              {fromBenefit
                ? '로그인하면 이 현장의 할인·혜택 조건을 바로 볼 수 있어요'
                : '신규 분양 현장을 쉽고 빠르게 찾아보세요'}
            </p>
          </div>

          {benefitIntent && (
            <div className="benefit-request-notice" data-testid="benefit-request-notice">
              <b>「{benefitIntent.title || '이 현장'}」 혜택 안내를 요청합니다</b>
              <span>
                로그인하면 이 현장의 할인·납부 혜택을 바로 보여 드려요. 이 현장 상담을 위해 담당자가 1533-8124로 연락드릴 수 있어요.
                (다른 현장 홍보는 아래 광고성 정보 수신에 동의한 경우에만 보내 드려요.)
              </span>
            </div>
          )}

          {error && <div className="error-message">{error}</div>}

          {/* 인앱 브라우저 감지 시 외부 브라우저 안내 */}
          {showInAppWarning && (
            <div className="inapp-browser-warning">
              <div className="warning-icon">⚠️</div>
              <p className="warning-message">{inAppBrowserInfo.message}</p>
              <p className="warning-instruction">{inAppBrowserInfo.instruction}</p>
              {isInKakaoTalk && (
                <>
                  <button
                    type="button"
                    className="open-external-btn"
                    onClick={handleOpenExternalBrowser}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                      <polyline points="15 3 21 3 21 9"></polyline>
                      <line x1="10" y1="14" x2="21" y2="3"></line>
                    </svg>
                    {isIOS() ? 'Safari로 열기' : '외부 브라우저로 열기'}
                  </button>
                  {showClipboardAlert && isIOS() && (
                    <div className="clipboard-alert">
                      <p>✅ URL이 복사되었습니다!</p>
                      <p>Safari 주소창을 <strong>길게 터치</strong>한 뒤<br/>"<strong>붙여넣기 및 이동</strong>"을 눌러주세요.</p>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          <div className="kakao-login-section">
            <p className="login-description">
              카카오 계정으로 간편하게 시작하세요
            </p>

            {/* 약관 동의 섹션 */}
            <div className="consent-section">
              {/* 전체 동의 */}
              <label className="consent-item consent-all">
                <input
                  type="checkbox"
                  checked={agreeAll}
                  onChange={(e) => handleAgreeAll(e.target.checked)}
                />
                <span className="checkmark"></span>
                <span className="consent-text">
                  전체 동의하기
                  <span className="consent-all-sub">필수 약관 + 광고성 정보 수신 동의(선택)</span>
                </span>
              </label>

              <div className="consent-divider"></div>

              {/* 개인정보 처리방침 (필수) */}
              <label className="consent-item">
                <input
                  type="checkbox"
                  checked={agreePrivacy}
                  onChange={(e) => setAgreePrivacy(e.target.checked)}
                />
                <span className="checkmark"></span>
                <span className="consent-text">
                  <span className="required">[필수]</span> 개인정보 처리방침 동의
                </span>
                <Link to="/privacy" className="consent-link" onClick={(e) => e.stopPropagation()}>
                  보기
                </Link>
              </label>

              {/* 서비스 이용약관 (필수) */}
              <label className="consent-item">
                <input
                  type="checkbox"
                  checked={agreeTerms}
                  onChange={(e) => setAgreeTerms(e.target.checked)}
                />
                <span className="checkmark"></span>
                <span className="consent-text">
                  <span className="required">[필수]</span> 서비스 이용약관 동의
                </span>
                <Link to="/terms" className="consent-link" onClick={(e) => e.stopPropagation()}>
                  보기
                </Link>
              </label>

              {/* 마케팅(광고성 정보) 수신 동의 (선택) — 고객 이익 중심 문구 + 카드 강조 (2026-10-05)
                  2026-10-08: '광고성 정보'를 이름에 밝힌다(정보통신망법 명시적 동의). 혜택 열람과는 무관 — 동의 안 해도 모든 서비스 이용(개인정보보호법 제22조⑤) */}
              <label className="consent-item consent-promo">
                <input
                  type="checkbox"
                  checked={agreeMarketing}
                  onChange={(e) => setAgreeMarketing(e.target.checked)}
                />
                <span className="checkmark"></span>
                <span className="consent-text">
                  <span className="promo-title">
                    <span className="optional">[선택]</span> 광고성 정보 수신 동의 (할인·분양 소식 받기) <span className="promo-badge">혜택</span>
                  </span>
                  <span className="promo-sub">
                    동의 회원에게는 <b>공개 전 잔여세대·할인율 변경</b>과 새 분양 소식을 카카오톡·문자·전화로 가장 먼저 알려드려요. 동의하지 않아도 혜택 열람 등 모든 서비스를 그대로 이용할 수 있고, 마이페이지에서 언제든 켜고 끌 수 있어요.
                  </span>
                </span>
                <Link to="/privacy#marketing" className="consent-link" onClick={(e) => e.stopPropagation()}>
                  보기
                </Link>
              </label>
            </div>

            <button
              type="button"
              className={`kakao-login-btn ${!canProceed ? 'disabled' : ''}`}
              onClick={handleKakaoLogin}
              disabled={loading || !canProceed}
            >
              {loading ? (
                <span className="loading-text">로그인 중...</span>
              ) : (
                <>
                  <img
                    src="https://developers.kakao.com/assets/img/about/logos/kakaolink/kakaolink_btn_small.png"
                    alt="카카오"
                    className="kakao-icon"
                  />
                  <span>카카오로 시작하기</span>
                </>
              )}
            </button>

            {!canProceed && (
              <p className="consent-warning">
                필수 약관에 동의하셔야 서비스를 이용하실 수 있습니다.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
